import { css } from 'lit';
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable promise/prefer-await-to-then */
/* eslint-disable no-use-before-define */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { AppSection, HttpStatusCode } from '@constants';
import { getSessionUUID } from '@utils/SessionStorageUtils';
import { CurrentEditorsDto, RoadEventDto } from '@typings/api';
import { APIConfig, DebuggingConfig } from '@/config/ConfigCARSx';
import HTTPMethod from 'http-method-enum';
import { APIRequest, getAPIHeaders } from '@/redux/APIRequest';
import { get, set } from 'idb-keyval';
import { ConfigREMApi, ConfigREMTable } from '../../config/ConfigREM';
import { APIRequestReturn } from '../../redux/APIRequest';
import store from '../../redux/redux-store';
import { getREMEventIcons, refreshREMEvent, setREMEvents } from '../../redux/rem/rem-actions-event';
import { getSign, getSignQueue } from '../../redux/rem/rem-actions-event-dms';
import {
	getAllCurrentEventEditors,
	setAllREMEventCurrentEditors,
	setREMEventCurrentEditors,
} from '../../redux/rem/rem-actions-event-editors';
import { selectCurrentDetailsChanged } from '../../redux/rem/rem-selectors';

const IDB_EVENTS_KEY = `${window.location.hostname}-rem-events-latest`;
const IDB_EVENTS_CURRENT_EDITORS_KEY = `${window.location.hostname}-rem-events-current-editors-latest`;

interface IDBEvents {
	data: RoadEventDto[];
	lastFetched: number;
}

interface IDBCurrentEditors {
	data: CurrentEditorsDto[];
	lastFetched: number;
}

let REMEventsPollingTimeout: ReturnType<typeof setInterval>;
let REMEventsLastAPIRequestReturn: APIRequestReturn;

const channel = new BroadcastChannel('events-poll');

let resolveFirstPoll: () => void;
let firstPollDone: Promise<void>;

channel.onmessage = (e) => {
	if (store.getState().routing.page !== AppSection.REM) {
		return;
	}

	if (e.data.type === 'EVENTS') {
		const events = e.data.payload as RoadEventDto[];
		void store.dispatch(getREMEventIcons(events));
		store.dispatch(setREMEvents(events));
		resolveFirstPoll?.();
	} else if (e.data.type === 'CURRENT_EDITORS') {
		store.dispatch(setAllREMEventCurrentEditors(e.data.payload as CurrentEditorsDto[]));
	}
};

const getEvents = async (): Promise<RoadEventDto[] | null> => {
	REMEventsLastAPIRequestReturn = await APIRequest(
		new Request(new URL(ConfigREMApi.events(), APIConfig.endpointURLBase).href, {
			method: HTTPMethod.GET,
			headers: new Headers({ ...getAPIHeaders() }),
		}),
	);

	if (REMEventsLastAPIRequestReturn.response?.status === HttpStatusCode.UNAUTHORIZED) {
		stopPollingREMEvents();
		return null;
	}

	try {
		if (REMEventsLastAPIRequestReturn.response?.ok) {
			return (await REMEventsLastAPIRequestReturn.response.json()) as RoadEventDto[];
		}
	} catch (error) {
		if (DebuggingConfig.showConsoleLogs) {
			console.error(`error parsing REM events:`, error);
		}
	}

	return null;
};

async function pollEvents() {
	try {
		const events = await getEvents();
		if (events === null) return;

		void store.dispatch(getREMEventIcons(events));
		store.dispatch(setREMEvents(events));
		channel.postMessage({ type: 'EVENTS', payload: events });
		void set(IDB_EVENTS_KEY, { data: events, lastFetched: Date.now() } satisfies IDBEvents);
	} catch (err) {
		if (DebuggingConfig.showConsoleLogs) {
			console.error(`error polling REM events:`, err);
		}
	}
}

async function pollEventsCurrentEditors() {
	try {
		const currentEditors = await getAllCurrentEventEditors();

		store.dispatch(setAllREMEventCurrentEditors(currentEditors));
		channel.postMessage({ type: 'CURRENT_EDITORS', payload: currentEditors });
		void set(IDB_EVENTS_CURRENT_EDITORS_KEY, {
			data: currentEditors,
			lastFetched: Date.now(),
		} satisfies IDBCurrentEditors);
	} catch (err) {
		if (DebuggingConfig.showConsoleLogs) {
			console.error(`error polling REM current editors:`, err);
		}
	}
}

async function pollEventInfo() {
	await Promise.allSettled([pollEvents(), pollEventsCurrentEditors()]);
}

const LEADER_KEY = 'events-poll-leader';
const LEADER_HEARTBEAT_MS = 3000;
const LEADER_TIMEOUT_MS = 8000;

const tabId = crypto.randomUUID();
let isLeader = false;
let heartbeatInterval: ReturnType<typeof setInterval> | undefined;
let electionCheckInterval: ReturnType<typeof setInterval> | undefined;
let currentPollingIntervalMs = ConfigREMTable.pollingDelayMs;

interface LeaderData {
	id: string;
	ts: number;
}

function readLeader(): LeaderData | null {
	try {
		const raw = localStorage.getItem(LEADER_KEY);
		if (raw) {
			const parsed = JSON.parse(raw) as LeaderData;

			if (!parsed.id || !parsed.ts) {
				localStorage.removeItem(LEADER_KEY);

				return null;
			}
			return parsed;
		}
	} catch {
		localStorage.removeItem(LEADER_KEY);
		return null;
	}

	return null;
}

function writeLeader() {
	localStorage.setItem(LEADER_KEY, JSON.stringify({ id: tabId, ts: Date.now() }));
}

function isLeaderStale(leader: LeaderData | null): boolean {
	return !leader || Date.now() - leader.ts > LEADER_TIMEOUT_MS;
}

async function becomeLeader() {
	if (isLeader) return;
	isLeader = true;

	writeLeader();
	heartbeatInterval = setInterval(writeLeader, LEADER_HEARTBEAT_MS);

	await pollEventInfo();
	resolveFirstPoll?.();
	REMEventsPollingTimeout = setInterval(pollEventInfo, currentPollingIntervalMs);
}

function resignLeadership() {
	if (!isLeader) return;
	isLeader = false;

	if (heartbeatInterval) clearInterval(heartbeatInterval);
	if (REMEventsPollingTimeout) clearInterval(REMEventsPollingTimeout);

	const current = readLeader();
	if (current?.id === tabId) {
		localStorage.removeItem(LEADER_KEY);
	}
}

function tryClaimLeadership() {
	if (isLeader) return;

	const current = readLeader();
	if (!isLeaderStale(current)) return;

	setTimeout(() => {
		if (isLeader) return;
		const check = readLeader();
		if (!isLeaderStale(check)) return;

		writeLeader();

		setTimeout(() => {
			const winner = readLeader();
			if (winner?.id === tabId) void becomeLeader();
		}, 50);
	}, Math.random() * 250);
}

export function stopPollingREMEvents() {
	if (electionCheckInterval) clearInterval(electionCheckInterval);

	resignLeadership();
}

export async function startPollingREMEvents(pollingIntervalMs = ConfigREMTable.pollingDelayMs) {
	currentPollingIntervalMs = pollingIntervalMs;

	firstPollDone = new Promise<void>((resolve) => {
		resolveFirstPoll = resolve;
	});

	const cachedEvents = (await get(IDB_EVENTS_KEY)) as IDBEvents;
	if (cachedEvents?.lastFetched) {
		void store.dispatch(getREMEventIcons(cachedEvents.data));
		store.dispatch(setREMEvents(cachedEvents.data));
		resolveFirstPoll();
	}

	const cachedCurrentEditors = (await get(IDB_EVENTS_CURRENT_EDITORS_KEY)) as IDBCurrentEditors;
	if (cachedCurrentEditors) {
		store.dispatch(setAllREMEventCurrentEditors(cachedCurrentEditors.data));
	}

	window.addEventListener('storage', (e) => {
		if (e.key === LEADER_KEY) tryClaimLeadership();
	});

	window.addEventListener('pagehide', resignLeadership);

	electionCheckInterval = setInterval(tryClaimLeadership, LEADER_HEARTBEAT_MS);

	tryClaimLeadership();

	await firstPollDone;
}

//	get one event

let REMEventPollingEnabled = false;
let REMEventPollingTimeout: ReturnType<typeof setTimeout>;
let REMEventPollingDelayMs: number;
let REMEventLastAPIRequestReturn: APIRequestReturn;
let REMEventPollingId: number;

export const pollREMEvent = async (): Promise<void> => {
	REMEventLastAPIRequestReturn = await store.dispatch(refreshREMEvent(REMEventPollingId));
	if (REMEventLastAPIRequestReturn.response?.status === HttpStatusCode.UNAUTHORIZED) {
		stopPollingREMEvent();
	}
	if (REMEventPollingEnabled === true) {
		REMEventPollingTimeout = setTimeout(() => {
			void pollREMEvent();
		}, REMEventPollingDelayMs);
	}
};

export const stopPollingREMEvent = (autoAbort = true): void => {
	REMEventPollingEnabled = false;
	window.clearTimeout(REMEventPollingTimeout);
	if (autoAbort && REMEventLastAPIRequestReturn) {
		REMEventLastAPIRequestReturn.abortController?.abort();
	}
};

export const startPollingREMEvent = async (
	eventId: number,
	pollingDelayMs: number,
	pollImmediately = true,
) => {
	if (REMEventPollingEnabled === true) {
		return; //	no overlapping polling!
	}
	REMEventPollingEnabled = true;
	REMEventPollingDelayMs = pollingDelayMs;
	REMEventPollingId = eventId;
	if (pollImmediately) {
		await pollREMEvent();
	} else {
		REMEventPollingTimeout = setTimeout(() => {
			void pollREMEvent();
		}, REMEventPollingDelayMs);
	}
};

//	get one sign

let REMSignPollingEnabled = false;
let REMSignPollingTimeout: ReturnType<typeof setTimeout>;
let REMSignPollingDelayMs: number;
let REMSignLastAPIRequestReturn: APIRequestReturn;
let REMSignPollingId: number;

export const pollREMSign = async (): Promise<void> => {
	//	TODO: why is api return request reused this way
	let apiRequestReturn = await store.dispatch(getSign(REMSignPollingId));
	apiRequestReturn = await store.dispatch(getSignQueue(REMSignPollingId));
	REMSignLastAPIRequestReturn = apiRequestReturn;

	if (REMSignLastAPIRequestReturn.response?.status === HttpStatusCode.UNAUTHORIZED) {
		stopPollingREMSign();
	}
	if (REMSignPollingEnabled === true) {
		REMSignPollingTimeout = setTimeout(() => {
			void pollREMSign();
		}, REMSignPollingDelayMs);
	}
};

export const stopPollingREMSign = (autoAbort = true): void => {
	REMSignPollingEnabled = false;
	window.clearTimeout(REMSignPollingTimeout);
	if (autoAbort && REMSignLastAPIRequestReturn) {
		REMSignLastAPIRequestReturn.abortController?.abort();
	}
};

export const startPollingREMSign = (
	signId: number,
	pollingDelayMs: number = ConfigREMApi.signPollingRate,
	pollImmediately = true,
): void => {
	if (REMSignPollingEnabled === true) {
		return; //	no overlapping polling!
	}
	REMSignPollingEnabled = true;
	REMSignPollingDelayMs = pollingDelayMs;
	REMSignPollingId = signId;
	if (pollImmediately) {
		void pollREMSign();
	} else {
		REMSignPollingTimeout = setTimeout(() => {
			void pollREMSign();
		}, REMSignPollingDelayMs);
	}
};

//	register current user

//	register current user

let REMHeartbeatEnabled = false;
let REMHeartbeatTimeout: ReturnType<typeof setTimeout>;
let REMHeartbeatDelayMs: number;
let REMHeartbeatLastAPIRequestReturn: APIRequestReturn;
let REMHeartbeatEventId: number;
let REMHeartbeatISReadOnly: boolean;

export const sendREMHeartbeat = async (isReadOnly = false): Promise<void> => {
	// don't alert other users that a change might be coming to their form if this form is out of date already
	const currentDetailsChanged = selectCurrentDetailsChanged(store.getState());
	if (!isReadOnly && !currentDetailsChanged) {
		REMHeartbeatLastAPIRequestReturn = await store.dispatch(
			setREMEventCurrentEditors(REMHeartbeatEventId, getSessionUUID()),
		);
		if (REMHeartbeatLastAPIRequestReturn.response?.status === HttpStatusCode.UNAUTHORIZED) {
			stopREMHeartbeat();
		}
	}
	if (REMHeartbeatEnabled === true) {
		REMHeartbeatTimeout = setTimeout(() => {
			void sendREMHeartbeat(isReadOnly);
		}, REMHeartbeatDelayMs);
	}
};

function REMHeartbeatVisibilityChangeHandler() {
	const currentDetailsChanged = selectCurrentDetailsChanged(store.getState());
	if (!document.hidden && !REMHeartbeatISReadOnly && !currentDetailsChanged) {
		void store
			.dispatch(setREMEventCurrentEditors(REMHeartbeatEventId, getSessionUUID()))
			.then((ret) => {
				REMHeartbeatLastAPIRequestReturn = ret;
				return ret;
			});
	}
}

export const stopREMHeartbeat = (autoAbort = true): void => {
	REMHeartbeatEnabled = false;
	window.clearTimeout(REMHeartbeatTimeout);
	if (autoAbort && REMHeartbeatLastAPIRequestReturn) {
		REMHeartbeatLastAPIRequestReturn.abortController?.abort();
	}
	document.removeEventListener('visibilitychange', REMHeartbeatVisibilityChangeHandler);
};

export const startREMHeartbeat = (eventId: number, delay: number, isReadOnly = false): void => {
	if (REMHeartbeatEnabled === true) {
		return; //	no overlapping polling!
	}
	REMHeartbeatEnabled = true;
	REMHeartbeatDelayMs = delay;
	REMHeartbeatEventId = eventId;
	REMHeartbeatISReadOnly = isReadOnly;
	void sendREMHeartbeat(isReadOnly);

	document.addEventListener('visibilitychange', REMHeartbeatVisibilityChangeHandler);
};
